// #region Imports
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { Theme } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  ConfirmResponse,
  useConfirmDialog
} from '_shared/_providers/dialogs/confirm/confirm-dialog';
import { useDialog } from '_shared/_providers/dialogs/dialog';
import { MRTNoMemo } from '_shared/_views/tables/table';
import Guid from '_shared/guids/guid';
import IndividualAvatar from 'marketplace/_views/avatars/individual-avatar';
import {
  useDeleteIndividualBookmarkMutation,
  useGetAllIndividualBookmarksQuery,
} from 'marketplace/bookmarks/_api/bookmark-api';
import { PageTab } from 'marketplace/bookmarks/_views/bookmarks-page-tabs';
import BookmarkedIndividual from 'marketplace/bookmarks/individual-bookmarks/bookmarked-individual';
import {
  getIndividualsByIds,
} from 'marketplace/individuals/_stores/individuals-redux-slice';
import { useGetIndividualsByIdsQuery } from 'marketplace/individuals/_api/individual-api';
import Individual from 'marketplace/individuals/individual';
import ViewIndividualProfile from 'marketplace/individuals/profiles/_views/individual-profile-container';
import {
  createMRTColumnHelper,
  type MRT_TableOptions,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton
} from "material-react-table";
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
// #endregion

// #region Styled Components
const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const RowTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  fontWeight: "600",
  marginLeft: theme.spacing(1),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
// #endregion

// #region Component Input Props
type IndividualBookmarksProps = {
  activeTab: PageTab;
}

const columnHelper = createMRTColumnHelper<BookmarkedIndividual>();

export default function IndividualBookmarks(props: Readonly<IndividualBookmarksProps>) {
  const { activeTab } = props;

  // #region Hooks
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const { openDialog } = useDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  // Bookmark Query
  const { data: bookmarksJson, isLoading: bookmarksLoading, isError: bookmarksError } = useGetAllIndividualBookmarksQuery();
  const [deleteIndividualBookmark, { isLoading: isDeleting }] = useDeleteIndividualBookmarkMutation();
  const bookmarks = useMemo(
    () => bookmarksJson?.map((b) => BookmarkedIndividual.fromJSON(b)).filter((b): b is BookmarkedIndividual => !!b) ?? [],
    [bookmarksJson],
  );

  // All individualIds to fetch
  const bookmarkedIndividualIds = useMemo(
    () => bookmarks?.map(b => b.individualId) ?? [],
    [bookmarks]
  );

  // Individual Selectors
  const individuals = useSelector(getIndividualsByIds(bookmarkedIndividualIds));
  const { isLoading: individualsLoading, isError: individualsError } = useGetIndividualsByIdsQuery(
    { ids: bookmarkedIndividualIds.map(id => id.value) },
    { skip: !bookmarkedIndividualIds.length }
  );

  const individualsById = useMemo(() => {
    const map: Record<string, Individual> = {};
    for (const ind of individuals) {
      map[ind.id.value] = ind;
    }
    return map;
  }, [individuals]);

  // #region Lifecycle Hooks
  useEffect(() => {
    if (bookmarksError) {
      enqueueSnackbar("Error loading bookmarks. Please try again.", { variant: 'error' });
    }
  }, [bookmarksError]);

  // #region Event Handlers
  async function handleDeleteBookmark(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    bookmark: BookmarkedIndividual
  ) {
    event.stopPropagation();

    const response = await confirm({
      title: 'Delete Bookmark?',
      message: 'Do you want to delete?',
      okButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await deleteIndividualBookmark({
        bookmarkId: bookmark.id.value,
      }).unwrap();
      enqueueSnackbar("Deleted bookmark", { variant: 'success' });
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Couldn't delete bookmark. Please try again", { variant: 'error' });
    }
  }

  function handleViewProfile(profileId?: Guid): void {
    if (!profileId) return;
    openDialog({
      component: (
        <ViewIndividualProfile
          individualId={profileId}
        />
      ),
      titleStyle: {
        paddingBottom: 0
      },
      contentSxProps: {
        display: 'flex',
        overflowX: 'hidden'
      },
      MuiProps: {
        maxWidth: 'lg',
        fullWidth: false
      }
    });
  }
  // #endregion

  // #region Columns
  const renderNameColumn = useCallback((bookmark: BookmarkedIndividual) => {
    const id = bookmark.individualId;
    const individual = individualsById[id.value];
    const loadingText = individualsLoading ? "Loading..." : "Unknown Individual";

    const name =
      individual?.fullName ??
      (individualsError ? "Error loading" : loadingText);

    return (
      <NameCellContainer>
        <IndividualAvatar
          individual={individual}
        />
        <RowTitle>{name}</RowTitle>
      </NameCellContainer>
    );
  }, [
    individualsById,
    individualsLoading,
    individualsError
  ]);

  const columns = useMemo(() => [
    columnHelper.display({
      header: "Name",
      id: "name",
      Cell: ({ row }) => renderNameColumn(row.original),
    }),
    columnHelper.accessor((bookmark) => bookmark.createdDate.format("MM/DD/YY"), {
      header: "Bookmarked On",
      id: "created",
    }),
  ], [renderNameColumn]);
  // #endregion

  // #region Table
  const tableOptions: MRT_TableOptions<BookmarkedIndividual> = {
    columns,
    data: bookmarks ?? [],
    enableRowSelection: false,
    enableTableHead: true,
    enableColumnPinning: true,
    enableRowActions: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => handleViewProfile(row.original.individualId),
      sx: { cursor: "pointer" },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">
            Bookmarks
          </TableTitle>
          <LowerTopToolbarContainer>
            <Tabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_event, newValue) => navigate(`/marketplace/bookmarks/${newValue}`)}>
              <Tab value={PageTab.Individual} label="Individual" />
              <Tab value={PageTab.Team} label="Team" />
              <Tab value={PageTab.Company} label="Company" />
            </Tabs>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <ToggleGlobalFilterButton table={table} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleDensePaddingButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </ToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    getRowId: (row) => row.id?.value ?? row.individualId?.value,
    initialState: {
      columnPinning: { right: ['mrt-row-actions'] }
    },
    renderRowActions: ({ row }) => (
      <Tooltip title="Delete Bookmark">
        <IconButton
          onClick={(event) => handleDeleteBookmark(event, row.original)}
        >
          <DeleteIcon color="error" />
        </IconButton>
      </Tooltip>
    ),
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: {
      sx: {
        flex: 1
      },
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column"
      },
    },
    muiToolbarAlertBannerProps: !bookmarksError
      ? undefined
      : {
        color: "error",
        children: "Error loading data",
      },
    // onColumnFiltersChange: setColumnFilters,
    // onGlobalFilterChange: setGlobalFilter,
    // onPaginationChange: setPagination,
    // onSortingChange: setSorting,
    rowCount: bookmarks?.length,
    state: {
      // columnFilters,
      // globalFilter,
      isLoading: bookmarksLoading || isDeleting,
      // pagination,
      showAlertBanner: bookmarksError || individualsError,
      showProgressBars: bookmarksLoading || isDeleting,
      // sorting,
    },
  }

  return <MRTNoMemo<BookmarkedIndividual> options={tableOptions} />
}
